/*
 This file is part of GNU Taler
 (C) 2025 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import { opUnknownFailure, TalerError } from "@gnu-taler/taler-util";
import { useCallback, useEffect, useState } from "preact/hooks";

/**
 * convert the async function into preact hook
 *
 * @param callback the async function
 *
 * @returns operation status
 */
export function useAsync<Res>(
  callback: (() => Promise<Res>) | undefined,
  deps: Array<any> = [],
) {
  const [data, setData] = useState<Res>();
  const [error, setError] = useState<TalerError>();

  useEffect(() => {
    let unloaded = false;
    if (callback) {
      callback()
        .then((resp) => {
          if (unloaded) return;
          setData(resp);
        })
        .catch((error: unknown) => {
          if (unloaded) return;
          if (error instanceof TalerError) {
            setError(error);
          } else {
            setError(TalerError.fromException(error));
          }
        });
    }
    return () => {
      unloaded = true;
    };
  }, deps);

  if (error) return error;
  if (!data) return undefined;
  return data;
}

export const LONG_POLL_DELAY = 15000;

// FIXME: the problem with this compared with useSWR is that
// if the hook is called from more than one place then
// you will have multiple request. This needs to be merged, maybe based on a
// key
/**
 * First start with `initial` value, if initial is undefined then finish.
 * Otherwise:
 * Based on `initial` check if it should do long-polling with `shouldRetryFn`
 * If the result is undefined then finish.
 * Otherwise:
 * Verify if the call is going to fast, if so slow down.
 *
 * Call `retryFn` as the long poll function.
 * The result will be the next `initial` value.
 *
 *
 *
 * @param fetcher fetcher should be a memoized function
 * @param retry
 * @param deps
 * @returns
 */
export function useLongPolling<Res, Rt>(
  initial: Res,
  shouldRetryFn: (res: Res, count: number) => Rt | undefined,
  retryFn: (last: Rt, deps: Array<any>) => Promise<Res>,
  deps: Array<any> = [],
  opts: { minTime?: number } = {},
) {
  const minTime = opts?.minTime ?? 1000;

  const [retry, setRetry] = useState<{
    // count: number;
    fn: (() => Promise<Res>) | undefined;
    startMs: number | undefined;
  }>({
    // count: 0,
    fn: undefined,
    startMs: undefined,
  });

  const result = useAsync(retry.fn, [retry.startMs, ...deps]);

  const body = result ?? initial;

  const doRetry = useCallback((rt: Rt) => {
    return function doRetryImpl() {
      // call again
      setRetry((lt) => ({
        // count: lt.count + 1,
        fn: () => retryFn(rt, deps),
        startMs: new Date().getTime(),
      }));
    };
  }, deps);

  useEffect(() => {
    if (body === undefined || body instanceof TalerError) return;
    const _body = body;

    const rt = shouldRetryFn(_body, retry.startMs ?? 0);
    if (rt === undefined) return;

    if (retry.startMs === undefined) {
      doRetry(rt);
    } else {
      const diff = new Date().getTime() - retry.startMs;
      if (diff < minTime) {
        // calling too fast, wait whats left to reach minTime
        delayMs(minTime - diff).then(() => doRetry(rt));
      } else {
        doRetry(rt);
      }
    }
  }, [body]);

  return body;
}

/**
 * this should be in taler-utils
 * @param ms
 * @returns
 */
export async function delayMs(ms: number): Promise<void> {
  return new Promise((resolve, reject) => {
    setTimeout(() => resolve(), ms);
  });
}

/**
 * If the function `fn` is called faster than `minTime` wait
 * before calling again.
 *
 * Helps to prevent burst calling when the server does not implement
 * long polling correctly.
 *
 * @returns a wrapper of `fn` function
 */
export function preventBurst<type, args>(
  fn: (...args: args[]) => Promise<type>,
  opts: {
    minTimeMs?: number;
    errorTimeMs?: number;
  } = {},
): typeof fn {
  const mt = opts.minTimeMs ?? 3000;
  const et = opts.errorTimeMs ?? mt;

  let nextCallShouldWait = 0;
  console.log("nooo");
  return async (...args): Promise<type> => {
    const start = new Date().getTime();
    console.log("que onda", nextCallShouldWait);
    if (nextCallShouldWait > 0) {
      console.log("waiting", nextCallShouldWait);
      await delayMs(nextCallShouldWait);
      nextCallShouldWait = 0;
    }
    const r = fn(...args)
      .then((result) => {
        const diff = new Date().getTime() - start;
        nextCallShouldWait = Math.max(0, mt - diff);
        console.log(
          "next call",
          nextCallShouldWait,
          diff,
          mt,
          Math.max(0, mt - diff),
        );
        return result;
      })
      .catch((error) => {
        const diff = new Date().getTime() - start;
        console.log(
          "error call",
          nextCallShouldWait,
          diff,
          et,
          Math.max(0, et - diff),
        );
        nextCallShouldWait = Math.max(0, et - diff);
        throw error;
      });
    return r;
  };
}
